import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { APP_TOKEN_KEY, logoutAppUser } from "./appAuthSlice";
import {
  OPEN_GAME_STATUSES,
  mergeGamesInList,
  upsertGameInList,
  wasUserInGame,
  isRecentSettledGame,
  getGameWheelEntries,
  getGameId,
  canJoinGame,
  canLeaveGame,
  findUserEntry,
  findUserParticipationEntry,
  isUserCreator,
} from "../utils/bondWheelHelpers";
import {
  runBondWheelCreate,
  runBondWheelJoin,
  runBondWheelLeave,
} from "./bondWheelSocket";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(APP_TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const apiMessage = (error, fallback) =>
  error.response?.data?.error ||
  error.response?.data?.message ||
  error.message ||
  fallback;

export const fetchBondWheelConstants = createAsyncThunk(
  "bondWheel/fetchConstants",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/bondWheel/constants");
      return res.data;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Failed to load Bond Wheel rules"));
    }
  }
);

export const fetchBondWheelGames = createAsyncThunk(
  "bondWheel/fetchGames",
  async ({ status, limit = 50, skip = 0 } = {}, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/bondWheel/games", { params: { status, limit, skip } });
      return res.data;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Failed to load Bond Wheel games"));
    }
  }
);

export const fetchBondWheelGame = createAsyncThunk(
  "bondWheel/fetchGame",
  async ({ gameId }, { rejectWithValue }) => {
    try {
      const res = await api.get(`/api/bondWheel/games/${gameId}`);
      return res.data;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Failed to load Bond Wheel game"));
    }
  }
);

export const bondWheelCreate = createAsyncThunk(
  "bondWheel/create",
  async (payload, thunkApi) => runBondWheelCreate(payload, thunkApi)
);

export const bondWheelJoin = createAsyncThunk(
  "bondWheel/join",
  async (payload, thunkApi) => runBondWheelJoin(payload)(thunkApi.dispatch, thunkApi.getState)
);

export const bondWheelLeave = createAsyncThunk(
  "bondWheel/leave",
  async (payload, thunkApi) => runBondWheelLeave(payload)(thunkApi.dispatch, thunkApi.getState)
);

const initialState = {
  constants: null,
  games: [],
  selectedGame: null,
  lastSettledGame: null,
  loading: false,
  actionLoading: false,
  status: "idle",
  error: null,
};

const bondWheelSlice = createSlice({
  name: "bondWheel",
  initialState,
  reducers: {
    clearBondWheelError: (state) => {
      state.error = null;
    },
    setBondWheelSelectedGame: (state, { payload }) => {
      state.selectedGame = payload || null;
    },
    upsertBondWheelGame: (state, { payload }) => {
      if (!payload) return;
      state.games = upsertGameInList(state.games, payload);
      if (state.selectedGame?._id === payload._id) {
        state.selectedGame = { ...state.selectedGame, ...payload };
      }
    },
    setBondWheelLastSettledGame: (state, { payload }) => {
      state.lastSettledGame = payload || null;
      if (payload) {
        state.games = upsertGameInList(state.games, payload);
        if (state.selectedGame?._id === payload._id) {
          state.selectedGame = { ...state.selectedGame, ...payload };
        }
      }
    },
    setBondWheelActionLoading: (state, { payload }) => {
      state.actionLoading = !!payload;
    },
    resetBondWheelState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBondWheelConstants.fulfilled, (state, { payload }) => {
        state.constants = payload;
      })
      .addCase(fetchBondWheelGames.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBondWheelGames.fulfilled, (state, { payload, meta }) => {
        state.loading = false;
        const incoming = payload?.games || [];
        const statusFilter = meta.arg?.status;
        if (!statusFilter) {
          const incomingIds = new Set(
            incoming.map((g) => (g?._id != null ? String(g._id) : "")).filter(Boolean)
          );
          const kept = state.games.filter((game) => {
            if (!OPEN_GAME_STATUSES.includes(game.status)) return true;
            const id = game?._id != null ? String(game._id) : "";
            return incomingIds.has(id);
          });
          state.games = mergeGamesInList(kept, incoming);
        } else {
          state.games = mergeGamesInList(state.games, incoming);
        }
        state.status = "succeeded";
      })
      .addCase(fetchBondWheelGames.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload || "Failed to load Bond Wheel games";
        state.status = "failed";
      })
      .addCase(fetchBondWheelGame.fulfilled, (state, { payload }) => {
        const game = payload?.game || null;
        state.selectedGame = game;
        if (game) state.games = upsertGameInList(state.games, game);
      })
      .addCase(bondWheelCreate.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(bondWheelCreate.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(bondWheelCreate.rejected, (state, { payload }) => {
        state.actionLoading = false;
        state.error = payload?.error || payload?.message || payload || "Could not create Bond Wheel.";
      })
      .addCase(logoutAppUser.fulfilled, () => initialState);
  },
});

export const {
  clearBondWheelError,
  setBondWheelSelectedGame,
  upsertBondWheelGame,
  setBondWheelLastSettledGame,
  setBondWheelActionLoading,
  resetBondWheelState,
} = bondWheelSlice.actions;

export default bondWheelSlice.reducer;

export const selectBondWheelGames = (state) => state.bondWheel?.games || [];
export const selectBondWheelConstants = (state) => state.bondWheel?.constants;
export const selectBondWheelLoading = (state) => state.bondWheel?.loading;
export const selectBondWheelActionLoading = (state) => state.bondWheel?.actionLoading;
export const selectBondWheelError = (state) => state.bondWheel?.error;
export const selectBondWheelLastSettledGame = (state) => state.bondWheel?.lastSettledGame;

export const selectBondWheelGameById = (state, gameId) => {
  if (!gameId) return null;
  const id = String(gameId);
  const fromList = state.bondWheel.games.find((g) => getGameId(g) === id);
  const selected = state.bondWheel.selectedGame;
  if (selected && getGameId(selected) === id) {
    return fromList ? { ...fromList, ...selected } : selected;
  }
  return fromList || null;
};

export const selectMyBondWheelGames = (state, userId) => {
  if (!userId) return [];
  return state.bondWheel.games.filter((game) => {
    if (!wasUserInGame(game, userId)) return false;
    if (OPEN_GAME_STATUSES.includes(game.status)) return true;
    return isRecentSettledGame(game);
  });
};

export const selectBondWheelParticipation = (state, gameId, userId) => {
  const game = selectBondWheelGameById(state, gameId);
  const constants = state.bondWheel.constants;
  if (!game) {
    return { game: null, canJoin: false, canLeave: false, isCreator: false, isParticipant: false };
  }
  const entry = findUserEntry(game, userId) || findUserParticipationEntry(game, userId);
  return {
    game,
    isCreator: isUserCreator(game, userId),
    isParticipant: !!entry,
    canJoin: canJoinGame(game, userId, Date.now(), constants),
    canLeave: canLeaveGame(game, userId, Date.now(), constants),
  };
};

export const selectSelectedGamePlayerCount = (state, gameId) =>
  getGameWheelEntries(selectBondWheelGameById(state, gameId)).length;
